import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { forgotPasswordSchema } from './validations';
import { forgotPasswordApi } from '../../Services/authService';
import { whiteLogo } from '../../assets';
import { ErrorText } from '../../Components/index';

// Custom color constants
const primaryBlue = "#0e3584";

export const ForgotPassword = ({ handleBackLogin }) => {
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [apiResponseError, setApiResponseError] = useState(false);

  const form = useForm({
    resolver: yupResolver(forgotPasswordSchema),
    mode: 'all',
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleForgotPasswordClick = async (data) => {
    try {
      const email = data?.email.toLowerCase();
      const res = await forgotPasswordApi(email);

      if (res.status === 200) {
        setShowSuccess(true);
        setSnackbar({
          open: true,
          message: "Password reset link sent!\nCheck your spam folder if not in inbox.",
          severity: "success",
        });
      }
    } catch (error) {
      setApiResponseError(error?.response?.data?.error?.message);
    }
  };

  const handleEmailChange = (event) => {
    setApiResponseError(null);
    form.setValue("email", event.target.value);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        bgcolor: primaryBlue,
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: "center",
        justifyContent: "center",
        p: 0,
        m: 0,
      }}
    >

      {/* LEFT PANEL — visible on mobile (top) & desktop (left) */}
      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          pl: { xs: 2, md: 12, lg: 20 },
          py: { xs: 4, md: 0 },
          color: "#fff",
          bgcolor: primaryBlue,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#fff" }}>
          Intelli VKYC
        </Typography>

        <Typography sx={{ fontSize: 18, opacity: 0.9, mb: 4, color: "#fff" }}>
          Unified Verification for Modern Lending. Access APIs with confidence – 
          compliant, secure, and fraud-free.
        </Typography>

        <Stack spacing={2}>
          <Typography sx={{ fontSize: 18, opacity: 0.9, color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
            ✔️ Compliance-first onboarding
          </Typography>
          <Typography sx={{ fontSize: 18, opacity: 0.9, color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
            ⚡ Faster fraud detection
          </Typography>
          <Typography sx={{ fontSize: 18, opacity: 0.9,color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
            🔗 Access multiple APIs seamlessly
          </Typography>
          <Typography sx={{ fontSize: 18, opacity: 0.9,color: "#fff", display: "flex", alignItems: "center", gap: 1 }}>
            🏦 Trusted by leading BFIs & NBFCs
          </Typography>
        </Stack>
      </Box>

      {/* RIGHT PANEL — Reset Password Form */}
      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          minHeight: { xs: "auto", md: "100vh" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: { xs: 4, md: 0 },
        }}
      >
        <Paper
          elevation={4}
          sx={{
            borderRadius: "20px",
            px: { xs: 2, sm: 8 },
            py: { xs: 4, sm: 8 },
            width: "100%",
            maxWidth: 400,
            boxSizing: "border-box",
          }}
        >
          <Typography variant="h6" fontWeight={600} textAlign="center" mb={3}>
            Reset your password
          </Typography>

          <Typography variant="body1" textAlign="center" mb={3} color="text.secondary">
            Enter your email and we'll send a link to reset your password.
          </Typography>

          <form onSubmit={handleSubmit(handleForgotPasswordClick)} autoComplete="off">
            <Stack spacing={3}>
              {apiResponseError && <ErrorText>{apiResponseError}</ErrorText>}

              <TextField
                fullWidth
                label="Email address"
                type="email"
                placeholder="Enter your email"
                {...register("email")}
                onChange={handleEmailChange}
                error={!!errors.email}
                helperText={errors.email?.message}
                InputLabelProps={{ shrink: true, style: { fontSize: 14 } }}
                InputProps={{ style: { fontSize: 13 } }}
                sx={{
                  "& .MuiInputBase-root": {
                    height: 40,
                    backgroundColor: "#fff",
                  },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ fontSize: 18, fontWeight: 600, py: 1.3 }}
              >
                Send reset link
              </Button>

              <Button
                sx={{ alignSelf: "center", textTransform: "none", fontSize: 16 }}
                onClick={handleBackLogin}
              >
                Back to Sign In
              </Button>
            </Stack>
          </form>

          <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleSnackbarClose}
          >
            <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
              {snackbar.message}
            </Alert>
          </Snackbar>

          <Box mt={6} textAlign="center">
            <Typography variant="body2" color="text.secondary" fontSize={16}>
              powered by{" "}
              <a
                href="https://www.habilelabs.io/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#2ab6e5", fontWeight: 500, textDecoration: "none" }}
              >
                HabileLabs
              </a>
            </Typography>
          </Box>
        </Paper>
      </Box>

    </Box>
  );
};
